import { useEffect, useState } from "react";

import { App } from "../../../canvas/src/components/NodeSidebar/AppTypeahead/useAppSearchData";

// Define the types for the user context payload
export interface UserContextPayload {
  activity__pageview_properties?: PageViewProperties;
  user__role_org_info?: UserRoleOrgInfo;
  apps__authenticated_apps?: AuthenticatedApps;
}

export interface PageViewProperties {
  last_updated: string;
  first_event_datetime: string;
  customuser_ids: string[];
  countries: string[];
  device_ietf_language_codes: string[];
  timezones: string[];
  mobile_only: boolean;
  desktop_only: boolean;
  os_families: string[];
  pagepaths_clean: string[];
  service_slugs: string[];
  zap_template_slugs: string[];
  utm_mediums: string[];
  utm_campaigns: string[];
  hostnames: string[];
}

export interface UserRoleOrgInfo {
  last_updated: string;
  role: string;
  job_level: string;
  company_size: string;
}

export interface AuthenticatedApps {
  last_updated: string;
  value: string[];
}

const fetchApps = async (slugs: string[]) => {
  try {
    const response = await fetch(
      `/api/canvasui/integrations?slugs=${slugs.join(",")}`,
    );
    const data = await response.json();

    return data.results;
  } catch (err) {
    console.error("Error fetching apps:", err);
    return [];
  }
};

export const useUserContext = () => {
  const [connectedApps, setConnectedApps] = useState<App[]>([]);
  const [recommendedApps, setRecommendedApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchConnectedApps = async () => {
      try {
        setLoading(true);

        // Fetch user's connected apps
        const response = await fetch(
          "/api/canvasui/user-context?properties=activity__pageview_properties,apps__authenticated_apps,user__role_org_info",
        );

        if (!response.ok) {
          // throw new Error("Failed to fetch user connections");
        }

        const data: UserContextPayload = await response.json();

        // Process authenticated apps
        if (data.apps__authenticated_apps?.value?.length) {
          const apps = await fetchApps(data.apps__authenticated_apps.value);
          setConnectedApps(apps);
        }

        // Process service slugs
        if (data.activity__pageview_properties?.service_slugs?.length) {
          const apps = await fetchApps(
            data.activity__pageview_properties.service_slugs,
          );

          setRecommendedApps(apps);
        }
      } catch (err) {
        console.error("Error fetching user connections:", err);
        setError(err instanceof Error ? err : new Error("Unknown error"));
      } finally {
        setLoading(false);
      }
    };

    fetchConnectedApps();
  }, []);

  return { connectedApps, recommendedApps, loading, error };
};
